import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import MetricsCards from '../../widgets/cards/MetricsCards';
import FAQWidget from '../../widgets/interactive/FAQWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';
import { FeatureShowcaseCarouselOverlay } from '../../widgets/showcases/FeatureShowcase/FeatureShowcase';
import PlatformShowcase, { SegmentOption } from '../../widgets/showcases/PlatformShowcase';

type GasparForHRPageStaticProps = {
  plugins: StrapiApiPlugin[];
};

export const getStaticProps: GetStaticProps<GasparForHRPageStaticProps> = async (context) => {
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: GasparForHRPageStaticProps = { plugins };
  return withGlobalDefaultStaticProps(context, { props });
};

const GasparForHRPage: NextPageWithLayout<GasparForHRPageStaticProps> = (props) => {
  const { plugins } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.GasparForHR);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar for HR"
        title="AI helpdesk for impactful HR support"
        description="Let our Generative AI HR helpdesk tackle routine tasks, while your team empowers your people to thrive. Enhance employee engagement and transform your HR operations."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrowTop={true}>
        <MetricsCards
          records={[
            {
              metricValue: '100x',
              title: 'Faster onboarding',
              description:
                'Create a memorable onboarding journey for new team members, ensuring they navigate every step seamlessly. Empower HR teams to focus on creating a warm welcome, knowing that the onboarding process is automated, foolproof and fast.',
            },
            {
              metricValue: '85%',
              title: 'Increased employee happiness',
              description:
                'Experience a boost in employee satisfaction with our AI-powered HR helpdesk. Employees get instant, user-friendly assistance, minimising their frustration and unproductive time, and maximising their satisfaction and loyalty.',
            },
            {
              metricValue: '100%',
              title: 'Faster paid time off approvals',
              description:
                'Requesting time-off now is as smooth as a summer breeze! The process happens on one platform and the approval is instant. Your HR team saves valuable time as all systems are updated automatically.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.FeatureShowcaseOverlay}>
        <FeatureShowcaseCarouselOverlay
          title="HR helpdesk automation showcase"
          description="Dive into a showcase of our top automations in action"
          panels={[
            { imageUrl: CDNAssets.getUrl('for-hr-page-time-off-request'), label: 'Time off request' },
            { imageUrl: CDNAssets.getUrl('for-hr-page-pto-balance-request'), label: 'PTO balance request' },
            { imageUrl: CDNAssets.getUrl('for-hr-page-onboarding'), label: 'Onboarding' },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Transform your HR helpdesk with AI capabilities" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-hr-page-smart-automation'),
              orientation: 'left',
              title: 'Smart automation',
              description:
                'Automate tedious, repetitive tasks such as time-off requests with Generative AI. Give your HR team their time back to shine in employee engagement and talent acquisition, and empower employees to be more productive.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-hr-page-express-approvals'),
              orientation: 'right',
              title: 'Express approvals',
              description:
                'Speed up decision-making from days to minutes with a streamlined approval process on one platform. Instantly approve PTO and give access to calendars and folders.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-hr-page-swift-onboarding'),
              orientation: 'left',
              title: 'Swift onboarding',
              description:
                'Make onboarding an unforgettable experience. Craft custom, no-code workflows to automate the process, ensuring a smooth transition for new hires and allowing HR teams to foster a positive employee experience, without missing any crucial steps.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-hr-page-instant-answers'),
              orientation: 'right',
              title: 'Instant answers',
              description:
                'Empower your workforce to instantly find the information they are looking for: HR policies and benefits, how-to questions, and much more. All just a question away from our GenAI Virtual Agent!',
            },
            {
              imageUrl: CDNAssets.getUrl('for-hr-page-personalised-info'),
              orientation: 'left',
              title: 'Personalized info',
              description:
                'Offer employees quick and easy access to information tailored specifically to their needs and employment situation, such as their daily tasks, project deadlines, PTO balance.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.BlueElectric}>
        <PlatformShowcase
          images={{
            [SegmentOption.Slack]: CDNAssets.getUrl('for-hr-page-slack'),
            [SegmentOption.Teams]: CDNAssets.getUrl('for-hr-page-teams'),
            [SegmentOption.JIRA]: CDNAssets.getUrl('for-hr-page-jira'),
          }}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
GasparForHRPage.displayName = 'GasparForHRPage';
export default GasparForHRPage;
